import type { PolygonWithHoles, Ring, Vec2 } from '../../../../geo/types';
import type { Frame, MeshBuilder } from '../../../mesh/meshBuilder';
import type { BespokeGate, GateBlock, GatePassage } from '../../fortifications/bespokeGate';
import { layoutToWorld } from '../../../../core/coords';
import { difference, fromRing, union } from '../../../../geo/clip';
import { frustumSolid, horizontalCap, prismSolid, ringPoints } from '../../solids';
import { hipRoof } from '../../roofSolids';
import { corbelBand, finial, roofTrim, towerRoof, type FortressStyles } from './fortressParts';
import { archedBlock, panel } from './fortressBlocks';
import { FOOT, rectRing } from './sintAntoniespoortPlan';
import {
  GATEHOUSE,
  GATEHOUSE_WINDOWS,
  LAYOUT_BOX,
  REGULIERSPOORT_FRAME,
  REGULIER_PASSAGE,
  REGULIER_TOWER,
  REGULIER_TOWERS,
  TURRET,
  type RegulierTower,
} from './regulierspoortPlan';

/** A plan point (u along the passage toward the field, v to its left) in layout coordinates. */
export function regulierToLayout(p: Vec2): Vec2 {
  const { origin, heading } = REGULIERSPOORT_FRAME;
  const [c, s] = [Math.cos(heading), Math.sin(heading)];
  return [origin[0] + p[0] * c - p[1] * s, origin[1] + p[0] * s + p[1] * c];
}

/* Every face of an octagon, a vertex on no axis, so its faces meet the gatehouse's walls square on. */
const OCTAGON_PHASE = Math.PI / 8;

function gatehouse(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { u, v, eave, overhang } = GATEHOUSE;
  archedBlock(builder, styles.wall, frame, {
    u,
    v,
    foot: FOOT,
    top: eave,
    passage: REGULIER_PASSAGE,
  });
  const [u0, u1, v0, v1] = [u[0] - overhang, u[1] + overhang, v[0] - overhang, v[1] + overhang];
  const pitch = Math.atan2(GATEHOUSE.ridge - eave, (v1 - v0) / 2);
  hipRoof(builder, styles.roof, frame, { u0, u1, v0, v1, eaveY: eave, pitch });
  horizontalCap(builder, roofTrim(styles.roof), frame, rectRing([u0, u1], [v0, v1]), eave, false);
  const halfRidge = (u1 - u0 - (v1 - v0)) / 2;
  for (const end of [-halfRidge, halfRidge]) {
    finial(
      builder,
      styles.roof,
      frame,
      [end, 0],
      { base: GATEHOUSE.ridge, top: GATEHOUSE.needleTop },
      0.09
    );
  }
}

/* An octagonal turret on a corner: a corbel cone, its shaft through the eaves, a spire and a finial. */
function turret(builder: MeshBuilder, styles: FortressStyles, frame: Frame, corner: Vec2): void {
  const spec = { center: corner, radius: TURRET.corbelRadius, segments: 8, phase: OCTAGON_PHASE };
  frustumSolid(
    builder,
    styles.wall,
    frame,
    spec,
    TURRET.corbelFoot,
    TURRET.corbelTop,
    TURRET.radius,
    {
      top: false,
      bottom: true,
    }
  );
  const ring = ringPoints(corner, TURRET.radius, 8, OCTAGON_PHASE);
  prismSolid(builder, styles.wall, frame, ring, TURRET.corbelTop, TURRET.top, {
    top: true,
    bottom: false,
  });
  towerRoof(builder, styles.roof, frame, ring, { eave: TURRET.top, apex: TURRET.spire });
  finial(builder, styles.roof, frame, corner, { base: TURRET.spire, top: TURRET.finialTop }, 0.05);
}

function roundTower(
  builder: MeshBuilder,
  styles: FortressStyles,
  frame: Frame,
  tower: RegulierTower
): void {
  const { segments, eave, cornice, apex, finialTop } = REGULIER_TOWER;
  const ring = ringPoints(tower.centre, tower.radius, segments, 0);
  prismSolid(builder, styles.wall, frame, ring, FOOT, eave, { top: true, bottom: false });
  corbelBand(builder, styles.trim, frame, ring, cornice.reach, [eave - cornice.depth, eave]);
  const eaves = ringPoints(tower.centre, tower.radius + cornice.reach, segments, 0);
  towerRoof(builder, styles.roof, frame, eaves, { eave, apex });
  finial(builder, styles.roof, frame, tower.centre, { base: apex, top: finialTop }, 0.07);
}

interface Face {
  readonly at: (s: number) => Vec2;
  readonly facing: number;
  readonly along: readonly number[];
}

/* The fronts to the city and the field, and the flanks, each with the windows along it. */
function faces(): Face[] {
  const { u, v } = GATEHOUSE;
  const { fronts, flanks } = GATEHOUSE_WINDOWS;
  return [
    { at: (s) => [u[0], s], facing: Math.PI, along: fronts },
    { at: (s) => [u[1], s], facing: 0, along: fronts },
    { at: (s) => [s, v[0]], facing: -Math.PI / 2, along: flanks },
    { at: (s) => [s, v[1]], facing: Math.PI / 2, along: flanks },
  ];
}

function windows(builder: MeshBuilder, styles: FortressStyles, frame: Frame): void {
  const { width, rows } = GATEHOUSE_WINDOWS;
  for (const face of faces())
    for (const s of face.along)
      for (const row of rows) {
        panel(builder, styles.dark, frame, {
          ...row,
          at: face.at(s),
          facing: face.facing,
          width,
          arch: true,
          proud: 0.05,
          depth: 0.2,
        });
      }
}

/** The gate of 1538 on its plan (regulierspoortPlan.ts), in the frame of the layout's record. */
export function buildRegulierspoort(builder: MeshBuilder, styles: FortressStyles): void {
  const frame: Frame = {
    origin: layoutToWorld(REGULIERSPOORT_FRAME.origin, 0),
    yaw: REGULIERSPOORT_FRAME.heading,
  };
  gatehouse(builder, styles, frame);
  for (const u of GATEHOUSE.u) for (const v of GATEHOUSE.v) turret(builder, styles, frame, [u, v]);
  for (const tower of REGULIER_TOWERS) roundTower(builder, styles, frame, tower);
  windows(builder, styles, frame);
}

function toLayout(piece: PolygonWithHoles): PolygonWithHoles {
  return {
    outer: piece.outer.map(regulierToLayout),
    holes: piece.holes.map((h) => h.map(regulierToLayout)),
  };
}

/* Joined in the plan's own coordinates, where the towers cut into the gatehouse's flanks. */
function masonry(): readonly PolygonWithHoles[] {
  const rings: Ring[] = [
    rectRing(GATEHOUSE.u, GATEHOUSE.v),
    ...REGULIER_TOWERS.map((t) => ringPoints(t.centre, t.radius, REGULIER_TOWER.segments, 0)),
  ];
  return union(rings.map(fromRing));
}

/** The whole plan, its passage included: no lot reaches into it. */
export function regulierFootprint(): PolygonWithHoles {
  const whole = masonry();
  if (whole.length !== 1) throw new Error(`The gate's plan falls in ${whole.length} pieces`);
  return toLayout(whole[0]!);
}

/** The masonry less the passage (PLAN, Obstacles): one piece either side of it. */
export function regulierObstacles(): PolygonWithHoles[] {
  const way = rectRing([GATEHOUSE.u[0] - 0.1, GATEHOUSE.u[1] + 0.1], REGULIER_PASSAGE.v);
  return difference(masonry(), [fromRing(way)]).map(toLayout);
}

export function regulierPassage(): GatePassage {
  return {
    from: regulierToLayout([GATEHOUSE.u[0], 0]),
    to: regulierToLayout([GATEHOUSE.u[1], 0]),
    halfWidth: (REGULIER_PASSAGE.v[1] - REGULIER_PASSAGE.v[0]) / 2,
  };
}

/**
 * The town walls stop where they met the layout's box of the gate (site-geo's record), which ends
 * both legs inside the towers they run into.
 */
export function regulierBlock(): GateBlock {
  const { depth, width } = LAYOUT_BOX;
  return {
    centre: REGULIERSPOORT_FRAME.origin,
    heading: REGULIERSPOORT_FRAME.heading,
    half: [depth / 2, width / 2],
  };
}

export const REGULIERSPOORT: BespokeGate = {
  anchor: REGULIERSPOORT_FRAME.origin,
  build: buildRegulierspoort,
  footprint: regulierFootprint,
  obstacles: regulierObstacles,
  passage: regulierPassage,
  block: regulierBlock,
};
